/**
 * What the fleet looks like right now: one row per source, carrying when it was
 * last analysed here, how that run came out, and whose policy governed it.
 *
 * Pure. Everything is derived from rows the caller read out of Dexie, so the
 * whole thing is testable without a browser and cannot itself fail a read.
 *
 * Two constraints shape what this module will and will not say:
 *
 * - **A run is only attributable through its review.** `SavedAnalysis` is keyed
 *   by a hash, and the profile id is an INPUT to that hash rather than a field
 *   on the row, so it cannot be read back off the key. The recovery review
 *   stores `profileId` explicitly, and every run since 1.9.0 has one, because
 *   the analysis is held until a profile is chosen. An older cached row without
 *   a review is not guessed at — it is left out, and its source reads as never
 *   analysed. Attributing a run to the wrong source would be worse than showing
 *   none.
 * - **A stale override is not an active one.** `resolveEffectiveProfile`
 *   declines to apply an override whose `baseVersion` has fallen behind the
 *   repo profile, so a fleet view that showed it as governing policy would
 *   contradict the policy that actually ran. The two states are reported
 *   separately, in the same terms the resolver uses.
 */

import type { ProfileOverride } from "../../profiles/schema";

/** A run's headline outcome, as `AnalysisSummary.qualityGate` records it. */
export type FleetVerdict = "Pass" | "Warning" | "Quarantined";

/** The minimum of a cached analysis this module reads. */
export type FleetAnalysisRow = {
  /** When the row was cached. Used only when the review carries no timestamp. */
  createdAt: string;
  review?: { profileId: string; generatedAt?: string } | null;
  result: { summary: { qualityGate: FleetVerdict } };
};

/** A committed profile, as `listProfiles()` serves it. */
export type FleetProfileRow = {
  id: string;
  displayName: string;
  sourceUrl: string;
  agency?: string;
  version: number;
};

/** A locally measured draft, as the `draftProfiles` table stores it. */
export type FleetDraftRow = {
  draftId: string;
  createdAt: string;
  baselineFileName: string;
  latestFileName: string;
  delta: { displayName?: string; sourceUrl?: string; agency?: string };
};

/**
 * Whether local policy is amending the repo profile, in the resolver's own
 * terms: `active` is applied, `stale` was written against an older repo version
 * and is therefore NOT applied.
 */
export type FleetOverrideState = { revision: number; updatedAt: string; status: "active" | "stale" };

export type FleetRow = {
  id: string;
  displayName: string;
  sourceUrl: string;
  agency?: string;
  /** Repo version for a committed profile; absent for a draft, which has none. */
  version?: number;
  /**
   * `committed` — policy in `src/profiles/sources/`. `draft` — measured locally
   * and never committed, so it governs reporting and nothing else.
   */
  kind: "committed" | "draft";
  /** ISO-8601 of the most recent attributable run, or null when there is none. */
  lastAnalyzedAt: string | null;
  /** That run's quality gate. Null exactly when `lastAnalyzedAt` is null. */
  lastVerdict: FleetVerdict | null;
  /** Attributable runs still in the bounded analysis cache. Not a lifetime total. */
  cachedRunCount: number;
  /** Null when no override is stored for this profile. Never set on a draft. */
  override: FleetOverrideState | null;
  /** Set on a draft: the pair it was measured from, for the detail panel. */
  measuredFrom?: { baselineFileName: string; latestFileName: string; measuredAt: string };
};

export type FleetInput = {
  profiles: FleetProfileRow[];
  drafts?: FleetDraftRow[];
  overrides?: ProfileOverride[];
  analyses?: FleetAnalysisRow[];
};

/**
 * One row per source, sorted by display name.
 *
 * Committed profiles and drafts share the list because a fleet view that hid
 * drafts would misstate the fleet: since 1.9.0 a draft can be the policy a run
 * actually ran under.
 */
export function buildFleetRows(input: FleetInput): FleetRow[] {
  const runs = summarizeRuns(input.analyses ?? []);
  const overrides = new Map((input.overrides ?? []).map((entry) => [entry.profileId, entry]));

  const committed: FleetRow[] = input.profiles.map((profile) => ({
    id: profile.id,
    displayName: profile.displayName,
    sourceUrl: profile.sourceUrl,
    ...(profile.agency === undefined ? {} : { agency: profile.agency }),
    version: profile.version,
    kind: "committed" as const,
    ...runFacts(runs.get(profile.id)),
    override: overrideState(overrides.get(profile.id), profile.version)
  }));

  const drafts: FleetRow[] = (input.drafts ?? []).map((draft) => ({
    id: draft.draftId,
    displayName: draft.delta.displayName ?? draft.draftId,
    sourceUrl: draft.delta.sourceUrl ?? "",
    ...(draft.delta.agency === undefined ? {} : { agency: draft.delta.agency }),
    kind: "draft" as const,
    ...runFacts(runs.get(draft.draftId)),
    // A draft is never itself overridable — editing one means measuring again —
    // so an override row keyed by the same id could not have been applied to it.
    override: null,
    measuredFrom: {
      baselineFileName: draft.baselineFileName,
      latestFileName: draft.latestFileName,
      measuredAt: draft.createdAt
    }
  }));

  return [...committed, ...drafts].sort((a, b) => a.displayName.localeCompare(b.displayName));
}

type RunSummary = { at: string; verdict: FleetVerdict; count: number };

/**
 * Latest attributable run per profile.
 *
 * The review's `generatedAt` is preferred over the row's `createdAt`: it is when
 * the run happened, while `createdAt` is when it reached the cache. They differ
 * on a run cached late, and the analysed date is the one a person means.
 */
function summarizeRuns(analyses: FleetAnalysisRow[]): Map<string, RunSummary> {
  const latest = new Map<string, RunSummary>();
  for (const row of analyses) {
    const profileId = row.review?.profileId;
    if (profileId === undefined || profileId.length === 0) continue;
    const at = row.review?.generatedAt ?? row.createdAt;
    const seen = latest.get(profileId);
    if (seen === undefined) {
      latest.set(profileId, { at, verdict: row.result.summary.qualityGate, count: 1 });
      continue;
    }
    seen.count++;
    if (at.localeCompare(seen.at) > 0) {
      seen.at = at;
      seen.verdict = row.result.summary.qualityGate;
    }
  }
  return latest;
}

function runFacts(summary: RunSummary | undefined): Pick<FleetRow, "lastAnalyzedAt" | "lastVerdict" | "cachedRunCount"> {
  if (summary === undefined) {
    return { lastAnalyzedAt: null, lastVerdict: null, cachedRunCount: 0 };
  }
  return { lastAnalyzedAt: summary.at, lastVerdict: summary.verdict, cachedRunCount: summary.count };
}

/** Mirrors `resolveEffectiveProfile`: an override behind the repo version is stale, not applied. */
function overrideState(override: ProfileOverride | undefined, repoVersion: number): FleetOverrideState | null {
  if (override === undefined) return null;
  return {
    revision: override.revision,
    updatedAt: override.updatedAt,
    status: override.baseVersion === repoVersion ? "active" : "stale"
  };
}

/** Counts for the list header, so "13 profiles" does not quietly include drafts. */
export function summarizeFleet(rows: FleetRow[]): {
  total: number;
  committed: number;
  drafts: number;
  analysed: number;
  overridden: number;
  staleOverrides: number;
} {
  return {
    total: rows.length,
    committed: rows.filter((row) => row.kind === "committed").length,
    drafts: rows.filter((row) => row.kind === "draft").length,
    analysed: rows.filter((row) => row.lastAnalyzedAt !== null).length,
    overridden: rows.filter((row) => row.override?.status === "active").length,
    staleOverrides: rows.filter((row) => row.override?.status === "stale").length
  };
}
